import { useState } from 'react';

/**
 * useSort — sortable table hook. One sort-state + comparator implementation for
 * both hand-rolled tables and DataTable-driven pages.
 *
 * Usage (hand-rolled table, self-contained — the original form):
 *   const { sorted, toggle, ind } = useSort(rows, 'created_at', 'desc');
 *   <th onClick={() => toggle('name')}>Name{ind('name')}</th>
 *
 * Usage (DataTable page):
 *   const { sorted, field, dir, toggle } = useSort(rows, null, 'asc', {
 *     client: r => clientName(r.client_id),        // derived value
 *   });
 *   <DataTable rows={sorted} sort={{ field, dir, onSort: toggle }} ... />
 *
 * Usage (bucketed lists — active/closed halves sharing one sort state):
 *   const { sortRows, field, dir, toggle } = useSort(null, null, 'asc', accessors);
 *   const active = sortRows(activeRows); const closed = sortRows(closedRows);
 *
 * Params:
 *   rows       — array to sort for `sorted`; pass null when only using sortRows
 *   defaultKey — initial sort key. Pass null for "unsorted until a header is
 *                clicked" — sortRows/sorted then return the input order untouched
 *                (e.g. splitByStatusGroup's own flight_date_end ordering)
 *   defaultDir — 'asc' (default) | 'desc'
 *   accessors  — optional {key: row => value} for values not readable as a plain
 *                property (joined names, rollup counts). Missing keys fall back
 *                to row[key].
 *
 * Returns:
 *   sorted   — rows sorted by the current key + direction (input order if no key)
 *   sortRows — (list) => sorted copy under the same state; for sorting several
 *              lists (active/closed buckets) with one shared sort
 *   toggle   — (key) => void — click to sort/reverse by key
 *   ind      — (key) => ' ▲' | ' ▼' | ' ⇅' — indicator for hand-rolled headers
 *   field    — current sort key (null = unsorted); feeds DataTable's sort prop
 *   dir      — current direction; feeds DataTable's sort prop
 */
export default function useSort(rows, defaultKey = null, defaultDir = 'asc', accessors = {}) {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const toggle = (key) => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortRows = (list) => {
    if (!sortKey || !list) return list;
    const get = accessors[sortKey] || (r => r[sortKey]);
    return [...list].sort((a, b) => {
      const av = get(a) ?? '', bv = get(b) ?? '';
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  };

  const sorted = rows ? sortRows(rows) : rows;

  const ind = (key) => key === sortKey ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅';

  return { sorted, sortRows, toggle, ind, field: sortKey, dir: sortDir };
}
